import { CarrierBillingAccountType } from "@redotech/cockroachdb-codegen/congodb/carrier-billing-account-type";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";

import { t, useTranslation } from "@redotech/redo-i18n";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import {
  RedoModalFooter,
  RedoModalHeader,
} from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { memo } from "react";
import {
  billingAccountDefault,
  BillingAccountInput,
  CreateBillingAccountForm,
} from "./billing-account-form";
import { AddBillingAccountData } from "./billing-accounts";

interface AddBillingAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddAccount: (
    account: AddBillingAccountData,
    options?: { hidden?: boolean },
  ) => Promise<{ success: boolean }>;
  billingType: CarrierBillingAccountType;
  showUseOnceOption?: boolean;
}

function billingAccountModalCopy(billingType: CarrierBillingAccountType): {
  title: string;
  subtitle: string;
} {
  switch (billingType) {
    case CarrierBillingAccountType.ThirdParty:
      return {
        title: t("Add third party billing account"),
        subtitle: t(
          "Add third-party accounts to bill for specific shipments. Please ensure the account information is correct or you will not be able to buy labels.",
        ),
      };
    case CarrierBillingAccountType.Receiver:
      return {
        title: t("Add receiver billing account"),
        subtitle: t(
          "Bill the recipient's own carrier account for specific shipments. Please ensure the account information is correct or you will not be able to buy labels.",
        ),
      };
  }
}

export const AddBillingAccountModal = memo(function AddBillingAccountModal({
  isOpen,
  onClose,
  onAddAccount,
  billingType,
  showUseOnceOption,
}: AddBillingAccountModalProps) {
  const { t } = useTranslation();
  const form = useInput(BillingAccountInput, billingAccountDefault());

  const getAccountData = (): AddBillingAccountData => ({
    accountName: form.inputs.accountName.value,
    carrier: form.inputs.carrier.value,
    accountNumber: form.inputs.accountNumber.value,
    country: form.inputs.country.value,
    postalCode: form.inputs.postalCode.value,
    billingType,
  });

  const handleSubmit = useHandler(async () => {
    const result = await onAddAccount(getAccountData());
    if (result.success) {
      form.setValue(billingAccountDefault());
      onClose();
    }
  });

  const handleUseOnce = useHandler(async () => {
    const result = await onAddAccount(getAccountData(), { hidden: true });
    if (result.success) {
      form.setValue(billingAccountDefault());
      onClose();
    }
  });

  const canSubmit = form.allErrors.length === 0;
  const copy = billingAccountModalCopy(billingType);

  return (
    <RedoBaseModal isOpen={isOpen} onModalCloseRequested={onClose}>
      <RedoModalHeader
        cancelClicked={onClose}
        subtitle={copy.subtitle}
        title={copy.title}
      />

      <Flex dir="column" gap="xl" p="xl">
        <CreateBillingAccountForm billingType={billingType} form={form} />
      </Flex>

      {showUseOnceOption ? (
        <RedoModalFooter
          primaryButton={{
            disabled: !canSubmit,
            onClick: handleSubmit,
            text: t("Save & use"),
            size: "sm",
          }}
          secondaryButton={{
            disabled: !canSubmit,
            onClick: handleUseOnce,
            text: t("Use once"),
            size: "sm",
          }}
        />
      ) : (
        <RedoModalFooter
          primaryButton={{
            disabled: !canSubmit,
            onClick: handleSubmit,
            text: t("Submit"),
          }}
          secondaryButton={{ onClick: onClose, text: t("Cancel") }}
        />
      )}
    </RedoBaseModal>
  );
});
